/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Request Saga
 *
 * @author Mikhail Kokoulin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Alexander Golshtein
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.0.0
 */

import { createElement } from 'react';

import get from 'lodash/get';
import isString from 'lodash/isString';
import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import packageInfo from '../../../package.json';
import { setAppErrorCode } from '../modules/app/appActions';
import appRequest, { getResponseFromError, IFetchOptions, IResponseMeta } from '../modules/app/appRequest';
import { makeSelectAppFeatureEnabled } from '../modules/app/appSelectors';
import { EAppFeatures } from '../modules/app/appTypes';
import { makeSelectTranslationCommon } from '../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../modules/internationalization/types';
import { IErrorTranslation } from '../modules/internationalization/types/common/error';
import ModalErrorCommon from '../modules/modal/components/ModalError';
import ModalSuccess from '../modules/modal/components/ModalSuccess';
import { modalOpen } from '../modules/modal/modalActions';
import { IModalRenderProps } from '../modules/modal/types/modalTypes';
import { ECookieKey } from '../modules/storage/storageTypes';
import { getCookieByName } from '../modules/storage/storageUtils';
import { cacheUserDataStart } from '../pages/PageAuth/actions/authCacheActions';
import { openExternalRoute } from '../routes/constants/externalRoutes';
import { RoutePath } from '../routes/constants/routesConstants';

export interface IRequestHeader {
    [key: string]: string;
}

export interface IRequestOption {
    abortControllerId?: string;
    body?: any;
    credentials?: string;
    headers?: IRequestHeader;
    method?: string;
    mode?: string;
    skipUnauthorizedError?: boolean;
    throwIfHasError?: boolean;
}

export function* requestSaga(path: string, method: string, body?: any, options?: IRequestOption): SagaIterator {
    const csrfProtected = ['POST', 'PUT', 'DELETE'];

    let defauiltOptions: IRequestOption = {
        method: method,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Pragma': 'no-cache',
            'UI-Version': packageInfo.version,
        },
        mode: 'cors',
    };

    defauiltOptions = { ...defauiltOptions, ...options };
    const headers = get(defauiltOptions, 'headers.Content-Type', '');
    if (body) {
        defauiltOptions.body = isString(headers) && headers.includes('json') ? JSON.stringify(body) : body;
    }

    if (csrfProtected.includes(method)) {
        defauiltOptions.headers = {
            ...defauiltOptions.headers,
            'x-csrf-token': getCookieByName(ECookieKey.XSRF_TOKEN),
        };
    }

    const fetchOptions: IFetchOptions = {};
    if (options?.abortControllerId) {
        fetchOptions.abortControllerId = options.abortControllerId;
    }

    const response: IResponse & IResponseMeta = yield call(appRequest, path, defauiltOptions, fetchOptions);
    const { data, cached, error, unauthorized, version: currentVersion } = response;

    if (unauthorized && !options?.skipUnauthorizedError) {
        // После протухания токена необходимо принудительно обновлять страницу пользователя
        yield call(openExternalRoute, RoutePath.unauthorized);
    }

    // Проверка текущей версии приложения
    const checkUIVersion: boolean | undefined = yield select(
        makeSelectAppFeatureEnabled(EAppFeatures.showUIVersionModal),
    );
    if (
        // Текущая версия портала не совпадает с текущей
        currentVersion &&
        packageInfo.version !== currentVersion &&
        // доступность фичи
        checkUIVersion
    ) {
        const { modal, modalVersionUpdate }: ICommonTranslation = yield select(makeSelectTranslationCommon());
        yield put(
            modalOpen({
                onCloseCallback: () => {
                    location.reload();
                },
                render: (p: IModalRenderProps) =>
                    createElement(ModalSuccess, {
                        btn: modalVersionUpdate.btn,
                        children: modalVersionUpdate.text,
                        onCloseModal: p.onCloseModal,
                        title: modalVersionUpdate.title,
                        translation: modal,
                    }),
            }),
        );
    }

    // Start the caching process
    if (!cached) {
        yield put(cacheUserDataStart());
    }

    // Check errors
    if (error && error.code) {
        yield put(setAppErrorCode(error.code));
    }

    // Throw when has error
    if (options?.throwIfHasError && error) {
        throw response;
    }

    return data;
}

interface ICheckResponseErrorWithModalOptions {
    errorTranslation?: IErrorTranslation;
    message?: string; // Принудительное сообщение, независимо от кода ошибки
    title?: string; // Заголовок модального окна
    onCloseModal?: () => any;
}

export function* getResponseErrorMessage(error: any, options?: ICheckResponseErrorWithModalOptions): SagaIterator {
    const response: IResponse = yield call(getResponseFromError, error);
    const code = response?.error?.code || '';
    const common = yield select(makeSelectTranslationCommon());

    // default
    let codes = common.error.codes;
    let defaultMessage = common.error.defaultMessage;

    // custom
    if (options?.errorTranslation) {
        codes = {
            ...codes,
            ...options.errorTranslation.codes,
        };
        defaultMessage = options.errorTranslation.defaultMessage || defaultMessage;
    }

    return options?.message || codes[code] || defaultMessage;
}

export function* checkResponseErrorWithModal(error: any, options?: ICheckResponseErrorWithModalOptions): SagaIterator {
    const response: IResponse = yield call(getResponseFromError, error);
    const code = response?.error?.code || '';
    const message = response?.error?.message;
    const common: ICommonTranslation = yield select(makeSelectTranslationCommon());

    let codes = common.error.codes;
    let defaultMessage = common.error.defaultMessage;
    if (options?.errorTranslation) {
        codes = options.errorTranslation.codes;
        defaultMessage = options.errorTranslation.defaultMessage;
    }

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    dataName: 'modal-error-response',
                    onCloseModal: options?.onCloseModal || props.onCloseModal,
                    children: options?.message || codes[code] || message || defaultMessage,
                    title: options?.title || common.modal.error,
                    translation: common.modal,
                }),
        }),
    );

    return response;
}

export interface IResponse {
    data: any;
    error?: {
        code: string;
        message: string;
    };
}

export interface IErrorResponse extends Error {
    code?: number;
    response?: IResponse;
}
